import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';

import {
	isAIFCAction,
	isCreatableAction,
} from '@atlassian/conversation-assistant-action-helpers/utils';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import {
	isStoreAssistant,
	type StoreAssistant,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

export type PreviewStatus = 'preparing' | 'streaming' | 'complete';

export function findMessageById(
	messages: StoreChatItem[],
	messageId: string,
): StoreAssistant | undefined {
	return messages.find(
		(msg): msg is StoreAssistant =>
			isStoreAssistant(msg) && msg.content.message.message.id === messageId,
	);
}

export function findLastMessageWithCreateAction(
	messages: StoreChatItem[],
): StoreAssistant | undefined {
	for (let i = messages.length - 1; i >= 0; i--) {
		const msg = messages[i];
		if (
			isStoreAssistant(msg) &&
			(msg.content.message.message.actions || []).some(isCreatableAction)
		) {
			return msg;
		}
	}
	return undefined;
}

export function findLastMessageWithAIFCAction(
	messages: StoreChatItem[],
): StoreAssistant | undefined {
	for (let i = messages.length - 1; i >= 0; i--) {
		const msg = messages[i];
		if (isStoreAssistant(msg) && (msg.content.message.message.actions || []).some(isAIFCAction)) {
			return msg;
		}
	}
	return undefined;
}

export function findLastAssistantMessage(messages: StoreChatItem[]): StoreAssistant | undefined {
	for (let i = messages.length - 1; i >= 0; i--) {
		const msg = messages[i];
		if (isStoreAssistant(msg)) {
			return msg;
		}
	}
	return undefined;
}

export function extractContentId(message: StoreAssistant): string | undefined {
	const action = message.content.message.message.actions?.find(isCreatableAction);
	const contentId = (action?.data as { contentId: string | number } | undefined)?.contentId;
	// always make it a string since the final response sometimes returns a number and re-renders the iframe.
	return contentId === undefined ? undefined : String(contentId);
}

export function getFirstMessageAction(
	message: StoreAssistant | undefined,
): MessageAction | undefined {
	return message?.content?.message?.message?.actions?.[0];
}

export function extractTitle(message: StoreAssistant): string | undefined {
	const action = message?.content?.message?.message?.actions?.find(isCreatableAction);
	return (action?.data as { title: string } | undefined)?.title;
}

/**
 * Finds the current status of an AIFC action.
 * This function helps us determine the status and we use it to show the preview and other things like loading.
 * We do not want to show the preview until the the stream gives us streaming data or the streaming is complete.
 */
export function getAIFCActionStatus(
	message: StoreAssistant | undefined,
): PreviewStatus | undefined {
	if (!message) {
		return undefined;
	}

	const aifcAction = message?.content?.message?.message?.actions?.find(isAIFCAction);
	if (aifcAction) {
		const status = aifcAction?.status?.toLowerCase();
		if (status === 'preparing' || status === 'streaming') {
			return status as PreviewStatus;
		}

		// 'Planned' is the same as a final response. If we see it, assume streaming is complete.
		if (
			status === 'planned' ||
			status === 'finished' ||
			status === 'cancelled' ||
			message.content.streamingComplete
		) {
			return 'complete';
		}
	}

	return undefined;
}

function getStableMessageParts(message: StoreAssistant) {
	const action = message.content.message.message.actions?.find(isCreatableAction);
	const actionData = action?.data;

	// We just pick the parts we care about directly from the message
	// We avoid ADF since that is streamed and changes all the time.
	return {
		messageId: message.content.message.message.id,
		streamingComplete: message.content.streamingComplete,
		action: actionData ? pick(actionData, ['contentId', 'title']) : undefined,
		status: action?.status,
	};
}

export function areMessagesEqual(a?: StoreAssistant, b?: StoreAssistant): boolean {
	if (a === b) {
		return true;
	}
	if (!a || !b) {
		return false;
	}

	return isEqual(getStableMessageParts(a), getStableMessageParts(b));
}
